// The knowledge bank's claims the panel shows (knowledge-bank.md §11.9): per
// query (a goal's, the focused source's), read through the sanctioned port
// seam (`loadClaims`: one pure server read, never a command).
//
// Mirrors `useConversationGoals`: it re-reads whenever the queries or
// `worldKey` (the library value) or `turnKey` (the transcript length)
// change, so claims the agent records in a turn show live. A read in flight
// or a failed read keeps the previous value (the views never guess).
// Cancellation-guarded: the last key wins.

import { Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import type { Claim } from "../../../components/ClaimRow/ClaimRow.js";
import type { ClaimQuery } from "../../ports/workspace.js";
import type { AppPorts } from "../../ports.js";

type ClaimsRead = Awaited<ReturnType<AppPorts["loadClaims"]>>;

const keyOf = (q: ClaimQuery): string => {
    switch (q.type) {
        case "claims_of_source":
            return `source:${q.sourceId}`;
        case "claims_for_goal":
            return `goal:${q.goalId}`;
        case "claims_about_concept":
            return `concept:${q.conceptId}`;
    }
};

export const useClaims = (
    loadClaims: AppPorts["loadClaims"],
    queries: readonly ClaimQuery[],
    worldKey: unknown,
    turnKey: number,
): ((query: ClaimQuery) => readonly Claim[]) => {
    const [read, setRead] = useState<ReadonlyMap<string, readonly Claim[]>>(
        new Map(),
    );
    const key = queries.map(keyOf).join("\n");
    // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `queries` (a fresh array each render); `worldKey` and `turnKey` ARE the re-read signals.
    useEffect(() => {
        let cancelled = false;
        Promise.all(
            queries.map(
                async (q): Promise<readonly [string, ClaimsRead]> => [
                    keyOf(q),
                    await loadClaims(q),
                ],
            ),
        ).then((results) => {
            if (cancelled) return;
            setRead(
                (prev) =>
                    new Map(
                        results.map(([k, r]) => [
                            k,
                            Result.isOk(r) ? r.value : (prev.get(k) ?? []),
                        ]),
                    ),
            );
        });
        return () => {
            cancelled = true;
        };
    }, [loadClaims, key, worldKey, turnKey]);
    return (query) => read.get(keyOf(query)) ?? [];
};
